<script lang="ts">
    import route from '../Util/routes';
    import { round, createSettingsSubmitHandler } from '@/Util/utils';
    import TrainingLoad from './TrainingLoad.svelte';
    import ActivityDetailsTable from './ActivityDetailsTable.svelte';
    import { Button } from '@/lib/components/ui/button';

    export let profileParams;
    export let manualTrainingLoads;
    // Whether anything has been edited since it was shown or last saved, for the page's Save All
    export let changed = false;
    let responsivenessChanged = false, timeConstantsChanged = false, recoveryConstantsChanged = false, tableChanged = false;
    $: changed = responsivenessChanged || timeConstantsChanged || recoveryConstantsChanged || tableChanged;

    let ir_params={
        'pp':{'k1':0, 'tau1':0, 'k2':0, 'tau2':0,},
        'hie':{'k1':0, 'tau1':0, 'k2':0, 'tau2':0,},
        'ftp':{'k1':0, 'tau1':0, 'k2':0, 'tau2':0,},
        signature_decay_method:"1",
    };

    let ir_options = {
        auto_calc_training_responsiveness:true,
    }

    let trainingResponsiveness;
    let trainingLoadTimeConstants;
    let recoveryLoadTimeConstants;

    $: setIrParams(profileParams?.ir_params);  
    $: setIrOptions(profileParams);

    $: trainingLoadTimeConstants?.setTrainingLoad( getIrField(ir_params, 'tau1'));
    $: recoveryLoadTimeConstants?.setTrainingLoad( getIrField(ir_params, 'tau2'));
    $: trainingResponsiveness?.setTrainingLoad( getIrField(ir_params, 'k1'));

    function getIrField(ir_params, field){
        return {pp:ir_params['pp'][field], hie:ir_params['hie'][field], ftp:ir_params['ftp'][field]}
    }

    function setIrParams (val){
        if(!val) return

        ir_params['pp']['k1'] = round(val?.pp?.k1??0);
        ir_params['pp']['tau1'] = val?.pp?.tau1??0;
        ir_params['pp']['tau2'] = val?.pp?.tau2??0;

        ir_params['hie']['k1'] = round(val?.hie?.k1??0, 3);
        ir_params['hie']['tau1'] = val?.hie?.tau1??0;
        ir_params['hie']['tau2'] = val?.hie?.tau2??0;

        ir_params['ftp']['k1'] = round(val?.ftp?.k1??0, 1);
        ir_params['ftp']['tau1'] = val?.ftp?.tau1??0;
        ir_params['ftp']['tau2'] = val?.ftp?.tau2??0;
    }

    function setIrOptions (value){    
        if(!value) return
        ir_options.auto_calc_training_responsiveness = value?.auto_calc_resp
    }

    function getTrainingResponsiveness(){
        let Responsiveness = trainingResponsiveness?.getTrainingLoad();
        let auto_calc_training_responsiveness = trainingResponsiveness?.getBody()?.auto_calc;
        console.log(auto_calc_training_responsiveness);
        return {ir_params:
            {
                'pp':{'k1':Responsiveness?.pp},
                'hie':{'k1':Responsiveness?.hie},
                'ftp':{'k1':Responsiveness?.ftp},
            },  auto_calc_training_responsiveness
        };
    }

    function getTimeConstants(){
        let trainingTimeConstants = trainingLoadTimeConstants?.getTrainingLoad();
        let recoveryTimeConstants = recoveryLoadTimeConstants?.getTrainingLoad();

        return {ir_params:{
            'pp':{'tau1':trainingTimeConstants?.pp, 'tau2':recoveryTimeConstants?.pp}, 
            'hie':{'tau1':trainingTimeConstants?.hie, 'tau2':recoveryTimeConstants?.hie}, 
            'ftp':{'tau1':trainingTimeConstants?.ftp, 'tau2':recoveryTimeConstants?.ftp},
        }}
    }

    const postTrainingResponsiveness = route('UserController@postTrainingResponsiveness');
    const postTimeConstants = route('UserController@postTimeConstants');
    // Save All sends what has been edited: the sections whose values changed, and the activities opened in the table. A section
    // that fails doesn't keep the others from being sent.
    export async function submitHandler(){
        trainingLoadsTable?.saveAll();
        if (responsivenessChanged) await trainingResponsivenessSubmit();
        if (timeConstantsChanged || recoveryConstantsChanged) await timeConstantsSubmit();
    }
    // A request that fails (an error page, say) is said so, and its section stays to be saved
    const failed = (what: string) => (e: unknown) => {
        console.error(e);
        // @ts-ignore
        window.Xert?.warning?.(`${what} could not be saved.`);
        return false;
    };

    let _timeConstantsSubmitHandler = createSettingsSubmitHandler(postTimeConstants, getTimeConstants); //callbackFunction
    async function timeConstantsSubmit() {
        const sent = [trainingLoadTimeConstants?.currentSnapshot(), recoveryLoadTimeConstants?.currentSnapshot()];
        const sets = [trainingLoadTimeConstants?.serverSetCount(), recoveryLoadTimeConstants?.serverSetCount()];
        if (await _timeConstantsSubmitHandler().catch(failed('Time Constants')) === false) return;   // turned down or failed: still to be saved
        if (trainingLoadTimeConstants?.serverSetCount() === sets[0]) trainingLoadTimeConstants?.markSaved(sent[0]);
        if (recoveryLoadTimeConstants?.serverSetCount() === sets[1]) recoveryLoadTimeConstants?.markSaved(sent[1]);
    }
    
    // The Auto-Calc of the save on its way, for its response's values to be marked saved with
    let sentAutoCalc: boolean | null = null;
    let _trainingResponsivenessSubmitHandler = createSettingsSubmitHandler(postTrainingResponsiveness, getTrainingResponsiveness, (data)=>{ if(data?.ir_params)trainingResponsiveness?.setTrainingLoad( getIrField(data?.ir_params, 'k1'), sentAutoCalc);}); //callbackFunction
    async function trainingResponsivenessSubmit() {
        const sent = trainingResponsiveness?.currentSnapshot();
        sentAutoCalc = trainingResponsiveness?.getBody()?.auto_calc ?? null;
        const sets = trainingResponsiveness?.serverSetCount();
        if (await _trainingResponsivenessSubmitHandler().catch(failed('Training Responsiveness')) === false) return;
        // When the response brought values (Auto-Calc), they are what is saved, and setting them marked them so
        if (trainingResponsiveness?.serverSetCount() === sets) trainingResponsiveness?.markSaved(sent);
    }

    let trainingLoadsTable = null;
</script>

<div id="xpmc-settings-grid-container">
    <div id="xpmc-table" class="portlet light bordered full-height-content full-height-content-scrollable p-2 border-2">
        <ActivityDetailsTable bind:this={trainingLoadsTable} bind:changed={tableChanged} component={TrainingLoad} table={manualTrainingLoads} activityPickerTitle={"Choose Activity to Set Training Loads"}>
            <h4 slot="title">
                <span>Set Training Loads by Date/Activity</span>
                <a href="https://baronbiosys.com/glossary/manual_training_loads" target="_help">
                    <div class="tooltip-icon">
                        <i class="fa-regular fa-circle-question" aria-hidden="true"></i>
                    </div>
                </a>
                
            </h4>
        </ActivityDetailsTable>  
    </div>
    
    <div id="xpmc-training-responsiveness" class="portlet light bordered full-height-content full-height-content-scrollable p-2 border-2">
        <div  class="subtitle">
            <h4>{"Training Responsiveness"}</h4>
        </div>
        <div  class="w-full">
            <TrainingLoad bind:this={trainingResponsiveness} bind:changed={responsivenessChanged} title={"Training Responsiveness"} unitLabels={{pp:"Watts/TL", hie:"KJ/TL", ftp:"Watts/TL"}} bind:auto_calc={ir_options.auto_calc_training_responsiveness} smallSize={true} hideTitle={true} hideLabels={false} pp_min_step={1} hie_min_step={0.001} ftp_min_step={0.1} />
        </div>
        <div  class="flex ">
            <div class="flex grow flex-row justify-end">
                <Button disabled={!responsivenessChanged} on:click={trainingResponsivenessSubmit}>Save</Button>
            </div>
        </div>
    </div>
    <div id="xpmc-tc-container">
        <div id="xpmc-time-constants" class="portlet light bordered full-height-content full-height-content-scrollable p-2 border-2">
            <div id="xpmc-tc-title" class="subtitle">
                <h4>{"Time Constants"}</h4>
            </div>
            <div id="xpmc-tc-tl" class="w-full flex flex-row items-end m-1">
                <span class="mr-2 w-28 min-w-28 mb-[5px]">{"Training Load:"}</span> 
                <div class="grow">
                    <TrainingLoad bind:this={trainingLoadTimeConstants} bind:changed={timeConstantsChanged} title={"Training Load"} smallSize={true} lockable={false} readonly={false} hideTitle={true} hideLabels={false} pp_min_step={1} hie_min_step={1} ftp_min_step={1}></TrainingLoad>
                </div>
            </div>
            <div id="xpmc-tc-rl" class="w-full flex flex-row items-end m-1">
                <span class="mr-2 w-28 min-w-28 mb-[5px]">{"Recovery Load:"}</span> 
                <div class="grow">
                    <TrainingLoad bind:this={recoveryLoadTimeConstants} bind:changed={recoveryConstantsChanged} title={"Recovery Load"} smallSize={true} lockable={false} hideTitle={true} hideLabels={false} pp_min_step={1} hie_min_step={1} ftp_min_step={1}></TrainingLoad>
                </div>
            </div>
            <div id="xpmc-tc-save" class="flex mt-2">
                <div class="flex grow flex-row justify-end">
                    <Button disabled={!(timeConstantsChanged || recoveryConstantsChanged)} on:click={timeConstantsSubmit}>Save</Button>
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="scss">
    #xpmc-settings-grid-container{
        display:grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(3, auto);
        row-gap:16px;
        column-gap:16px;
    }
    #xpmc-tc-container{
            container-name: tc-container;
            container-type: inline-size;
        }

    #xpmc-training-responsiveness{
        height:fit-content;
    }

    @media only screen and (min-width: 530px) {

        #xpmc-table{
            grid-row: 3 / span 1;
            grid-column: 1 / span 2;
        }

        #xpmc-training-responsiveness{
            grid-row: 1 / span 1;
            grid-column: 2 / span 1;
        }

        #xpmc-tc-container{
            grid-row: 1 / span 1;
            grid-column: 1 / span 1;
        }
    }
    @media only screen and (max-width: 530px) {

        #xpmc-table{
            grid-row: 3 / span 1;
            grid-column: 1 / span 2;
        }

        #xpmc-training-responsiveness{
            grid-row: 1 / span 1;
            grid-column: 1 / span 2;
        }

        #xpmc-tc-container{
            grid-row: 2 / span 1;
            grid-column: 1 / span 2;
        }
    
    }

    @container tc-container (max-width: 600px) {
        #xpmc-time-constants{
            display:grid;
            grid-template-columns: repeat(2, fr);
            grid-template-rows: repeat(7, fr);
            column-gap:8px;
            #xpmc-tc-title{
                grid-column:1 / span 2;
                grid-row: 1 / span 2;
            }  
            
            #xpmc-tc-tl{
                display:block !important;
                grid-column: 1 / span 1;
                grid-row: 3 / span 4;
            } 

            #xpmc-tc-rl{
                display:block !important;
                grid-column: 2 / span 1;
                grid-row: 3 / span 4;
            } 
            
            #xpmc-tc-save{
                grid-column:1 / span 2;
                grid-row: 7 / span 1;
            }

        }
    }
</style>